<template>
    <AppLayout :userRole="activeUserRole" :imageLogo="defaultImage" headerTitle="Premises">
        <div class="mt-6 px-4 w-full bg-white text-PrimaryText h-full">
            <!-- Premises  tab -->
            <b-tabs v-model="activeTab">
                <b-tab-item label="Premises">
                    <!-- Filter Section for Premises -->
                    <div class="bg-white border border-gray-200 rounded-lg mb-4 p-4">
                        <div class="grid grid-cols-12 gap-4 items-end">
                            <!-- Name Filter -->
                            <div class="col-span-4 flex flex-col">
                                <label class="text-gray-700 font-medium mb-2">Premises Name</label>
                                <b-input
                                    v-model="premisesFilters.name"
                                    placeholder="Filter by premises name..."
                                    icon="magnify"
                                    @input="debouncedPremisesFilterChange"
                                    class="filter-input-large"
                                />
                            </div>
                            
                            <!-- Group Name Filter -->
                            <div class="col-span-4 flex flex-col">
                                <label class="text-gray-700 font-medium mb-2">Group Name</label>
                                <b-input
                                    v-model="premisesFilters.groupName"
                                    placeholder="Filter by group name..."
                                    icon="magnify"
                                    @input="debouncedPremisesFilterChange"
                                    class="filter-input-large"
                                />
                            </div>
                            
                            <!-- Clear Button -->
                            <div class="col-span-4 flex justify-center">
                                <b-button
                                    type="is-light"
                                    @click="clearPremisesFilters"
                                    icon-left="close"
                                    class="clear-filters-btn"
                                >
                                    Clear Filters
                                </b-button>
                            </div>
                        </div>
                    </div>
                    
                    <div class="columns">
                        <div class="column">
                            <table class="table is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Group</th>
                                        <th v-if="activeUserRole !== 'ROLE_AUDITOR'">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="prem in paginatedPremises" :key="prem.uuid">
                                        <td>{{ prem.name }}</td>
                                        <td>{{ prem.group.name }}</td>
                                        <td v-if="activeUserRole !== 'ROLE_AUDITOR'">
                                            <button class="button is-small" @click="editPremises(prem)">Edit</button>
                                            <button class="button is-small is-danger"
                                                @click="deletePremises(prem.uuid)">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="mt-4">
                                <nav class="flex justify-center">
                                    <ul class="pagination">
                                        <li v-if="currentPage > 1">
                                            <button class="pagination-previous" @click="prevPage()">Previous</button>
                                        </li>
                                        <li v-for="page in pages" :key="page">
                                            <button class="pagination-link" :class="{ 'is-current': currentPage === page }"
                                                @click="changePage(page)">{{ page }}</button>
                                        </li>
                                        <li v-if="currentPage < totalPages">
                                            <button class="pagination-next" @click="nextPage()">Next</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    </div>
                    <div class="add-button">
                        <b-button v-if="activeUserRole !== 'ROLE_AUDITOR'" @click="showPremisesModal('New')" type="is-primary">Add Premises</b-button>
                    </div>


                    <div class="fixed mt-10 z-50 inset-0 overflow-y-auto" v-if="showPremises">
                        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div class="fixed inset-0 transition-opacity">
                                <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>

                            <div
                                class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                                        {{ openForUpdate ? 'Update Premises' : 'New Premises' }}
                                    </h3>

                                    <div class="mt-3 sm:mt-0">
                                        <div class="mt-2">
                                            <div class="flex flex-col">
                                                <label for="name" class="mb-2 font-semibold text-gray-700">Name</label>
                                                <input id="name" v-model="newPremises.name" type="text"
                                                    placeholder="Enter Name"
                                                    class="border rounded-md text-gray-700 px-3 py-2 w-full">
                                            </div>
                                            <div class="flex flex-col mt-4">
                                                <label for="prem_group"
                                                    class="mb-2 font-semibold text-gray-700">Group</label>
                                                <div class="relative">
                                                    <select id="prem_group" v-model="newPremises.group"
                                                        class="border rounded-md appearance-none py-2 pl-3 pr-8 w-full text-gray-700">
                                                        <option v-for="option in groupOptions" :key="option.uuid"
                                                            :value="option">{{ option.name }}</option>
                                                    </select>
                                                    <div
                                                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                                        <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20">
                                                            <path d="M10 12l-6-6h12z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="flex flex-col mt-4">
                                                <label for="timezone"
                                                    class="mb-2 font-semibold text-gray-700">Timezone</label>
                                                <div class="relative">
                                                    <select id="timezone" v-model="newPremises.timezone"
                                                        class="border rounded-md appearance-none py-2 pl-3 pr-8 w-full text-gray-700">
                                                        <option v-for="option in timezoneOptions" :key="option"
                                                            :value="option">{{ option }}</option>
                                                    </select>
                                                    <div
                                                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                                        <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20">
                                                            <path d="M10 12l-6-6h12z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="button" @click="addPremises"
                                        class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-PrimaryDark text-base font-medium text-white hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm">
                                        {{ openForUpdate ? 'Update' : 'Create' }}
                                    </button>
                                    <button type="button" @click="hidePremisesModal"
                                        class="mt-3 w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab-item>

                <!-- Premises Group tab -->
                <b-tab-item label="Premises Group">
                    <div class="columns">
                        <div class="column">
                            <table class="table is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Organization</th>
                                        <th>Manager</th>
                                        <th v-if="activeUserRole !== 'ROLE_AUDITOR'">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="group in groups" :key="group.uuid">
                                        <td>{{ group.name }}</td>
                                        <td>{{ group.organization.name }}</td>
                                        <td>{{ group.manager.name }}</td>
                                        <td v-if="activeUserRole !== 'ROLE_AUDITOR'">
                                            <button class="button is-small" @click="editGroup(group)">Edit</button>
                                            <button class="button is-small is-danger"
                                                @click="deleteGroup(group.uuid)">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="add-button">
                        <b-button v-if="activeUserRole !== 'ROLE_AUDITOR'" @click="showPremisesGroupModal('New')" type="is-primary">Add Group</b-button>
                    </div>


                    <div class="fixed mt-10 z-50 inset-0 overflow-y-auto" v-if="showGroup">
                        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div class="fixed inset-0 transition-opacity">
                                <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>

                            <div
                                class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                                        {{ openForUpdate ? 'Update Group' : 'New Group' }}
                                    </h3>

                                    <div class="mt-3 sm:mt-0">
                                        <div class="mt-2">
                                            <div class="flex flex-col">
                                                <label for="group_name"
                                                    class="mb-2 font-semibold text-gray-700">Name</label>
                                                <input id="group_name" v-model="newGroup.name" type="text"
                                                    placeholder="Enter Name"
                                                    class="border rounded-md text-gray-700 px-3 py-2 w-full">
                                            </div>
                                            <div class="flex flex-col mt-4">
                                                <label for="group_org"
                                                    class="mb-2 font-semibold text-gray-700">Organization</label>
                                                <div class="relative">
                                                    <select id="group_org" v-model="newGroup.organization" @change="onChangeOrganization"
                                                        class="border rounded-md appearance-none py-2 pl-3 pr-8 w-full text-gray-700">
                                                        <option v-for="option in organizationOptions" :key="option.uuid"
                                                            :value="option">{{ option.name }}</option>
                                                    </select>
                                                    <div
                                                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                                        <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20">
                                                            <path d="M10 12l-6-6h12z" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>


                                        </div>
                                    </div>
                                </div>

                                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="button" @click="addGroup"
                                        class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-PrimaryDark text-base font-medium text-white hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm">
                                        {{ openForUpdate ? 'Update' : 'Create' }}
                                    </button>
                                    <button type="button" @click="hidePremisesGroupModal"
                                        class="mt-3 w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab-item>

                <!-- Premises Organization tab -->
                <b-tab-item label="Premises Organization">
                    <div class="columns">
                        <div class="column">
                            <table class="table is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th v-if="activeUserRole === 'ROLE_ROOT' || activeUserRole === 'ROLE_OWNER'">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="organization in organizations" :key="organization.uuid">
                                        <td>{{ organization.name }}</td>
                                        <td v-if="activeUserRole === 'ROLE_ROOT' || activeUserRole === 'ROLE_OWNER'">
                                            <button class="button is-small"
                                                @click="editOrganization(organization)">Edit</button>
                                            <button class="button is-small is-danger"
                                                @click="deleteOrganization(organization.uuid)">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <button v-if="activeUserRole === 'ROLE_ROOT' || activeUserRole === 'ROLE_OWNER'" class="button is-primary" @click="showOrganizationModal('New')">Add Organization</button>

                    <!-- Add Premises Organization Modal -->
                    <div class="fixed mt-10 z-50 inset-0 overflow-y-auto" v-if="showOrgnization">
                        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div class="fixed inset-0 transition-opacity">
                                <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>

                            <div
                                class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                                        {{ openForUpdate ? 'Update Organization' : 'New Organization' }}
                                    </h3>

                                    <div class="mt-3 sm:mt-0">
                                        <div class="mt-2">
                                            <div class="flex flex-col">
                                                <label for="org_name" class="mb-2 font-semibold text-gray-700">Name</label>
                                                <input id="org_name" v-model="newOrganization.name" type="text"
                                                    placeholder="Enter Name"
                                                    class="border rounded-md text-gray-700 px-3 py-2 w-full">
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="button" @click="addOrganization"
                                        class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-PrimaryDark text-base font-medium text-white hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm">
                                        {{ openForUpdate ? 'Update' : 'Create' }}
                                    </button>
                                    <button type="button" @click="hideOrganizationModal"
                                        class="mt-3 w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab-item>
            </b-tabs>
        </div>
    </AppLayout>
</template>
  
<script>
import AppLayout from "@/layouts/AppLayout";
import API from "@/services/API";
import { sync } from "vuex-pathify";
import ImageLogo from "@/assets/images/respro-logo.svg";
const _ = require('lodash');

export default {
    name: "premises",
    components: {
        AppLayout,
    },
    data() {
        return {
            defaultImage: ImageLogo,

            activeTab: 0,
            showPremises: false,
            showGroup: false,
            showOrgnization: false,
            currentPage: 1,
            itemsPerPage: 20,
            premisesTotalRows: 0,
            sortBy: 'name',
            sortDirection: 'asc',
            debouncedPremisesFilterChange: null,
            newPremises: {
                uuid: '',
                name: '',
                group: {
                    name: '',
                    uuid: ''
                },
                timezone: '',
            },
            newGroup: {
                name: '',
                organization: {
                    name: '',
                    uuid: ''
                },
                manager: {
                    name: '',
                    uuid: ''
                }
            },
            newOrganization: {
                name: '',
            },
            groupOptions: [],
            timezoneOptions: ['PST', 'MST', 'MDT', 'CST', 'EST'],
            organizationOptions: [],
            openForUpdate: false,
            filterManagers: [],
            isAvailableLocationModal: false

        };
    },
    computed: {
        premises: sync("premises/premises"),
        premisesPage: sync("premises/premisesPage"),
        premisesFilters: sync("premises/premisesFilters"),
        activeUserRole: sync("app/activeUserRole"),
        groups: sync("premises/groups"),
        managers: sync("users/managers"),
        organizations: sync("premises/organizations"),
        token: sync("app/session"),
        currentAuditIndex: sync("audit/currentAuditIndex"),
        totalPages() {
            return Math.ceil(this.premisesTotalRows / this.itemsPerPage);
        },
        paginatedPremises() {
            // Backend pagination - return all premises since they're already paginated
            return this.premises;
        },
        pages() {
            const pages = [];
            for (let i = 1; i <= this.totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }
    },
    methods: {
        debounce(func, delay) {
            let timeoutId;
            return (...args) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => func.apply(this, args), delay);
            };
        },

        clearPremisesFilters() {
            this.premisesFilters.name = '';
            this.premisesFilters.groupName = '';
            this.currentPage = 1;
            this.premisesPage = 1;
            this.fetchPremisesPaginated();
        },

        onChangeOrganization() {
            if (this.newGroup.organization && this.newGroup.organization.uuid) {
                this.filterManagers = this.managers.filter(manager =>
                    manager.organizations && manager.organizations.some(org => org.uuid === this.newGroup.organization.uuid)
                );
            } else {
                this.filterManagers = [];
            }
            if (this.newGroup.manager && this.newGroup.manager.uuid) {
                if (!this.filterManagers.some(m => m.uuid === this.newGroup.manager.uuid)) {
                    this.newGroup.manager = { name: '', uuid: '' };
                }
            }
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.premisesPage = this.currentPage;
                this.fetchPremisesPaginated();
            }
        },
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.premisesPage = this.currentPage;
                this.fetchPremisesPaginated();
            }
        },
        changePage(page) {
            this.currentPage = page;
            this.premisesPage = page;
            this.fetchPremisesPaginated();
        },


        showPremisesModal(isNew) {
            this.hidePremisesGroupModal()
            this.hideOrganizationModal()
            if (isNew == "New") {
                this.newPremises = {
                    name: '',
                    group: null,
                    timezone: 'MST',
                }
                this.openForUpdate = false;
            } else this.openForUpdate = true
            this.showPremises = true
        },

        hidePremisesModal() {
            this.newPremises = {
                name: '',
                group: null
            }
            this.showPremises = false
        },

        showPremisesGroupModal(isNew) {
            this.hidePremisesModal()
            this.hideOrganizationModal()
            if (isNew == "New") {
                this.newGroup = {
                    name: '',
                    organization: {
                        name: '',
                        uuid: ''
                    },
                manager: {
                    name: '',
                    uuid: ''
                }
                }
                this.openForUpdate = false;
            } else this.openForUpdate = true
            this.showGroup = true
        },

        hidePremisesGroupModal() {
            this.newGroup = {
                name: '',
                organization: {
                    name: '',
                    uuid: ''
                }
            }
            this.showGroup = false
        },

        showOrganizationModal(isNew) {
            this.hidePremisesGroupModal()
            this.hidePremisesModal()
            if (isNew == "New") {
                this.newOrganization = {
                    name: '',
                }
                this.openForUpdate = false;
            } else this.openForUpdate = true
            this.showOrgnization = true
        },

        hideOrganizationModal() {
            this.newOrganization = {
                name: '',
            }
            this.showOrgnization = false
        },

        addPremises() {

            const loadingComponent = this.$buefy.loading.open({ container: null });

            const data = {
                name: this.newPremises.name,
                group: this.newPremises.group.uuid,
                timezone: this.newPremises.timezone,
            }

            if (!this.openForUpdate) {

                API.create_premises(this.token, data)
                    .then((responses) => {

                        this.fetchPremises()

                        // Also refresh user's personal premises list
                        this.$store.dispatch('app/refreshPremises').then(() => {
                            console.log('User premises refreshed after creating premise');
                        }).catch(error => {
                            console.error('Failed to refresh user premises:', error);
                        });

                        loadingComponent.close();

                        this.showPremises = false

                        if (responses)
                            this.$buefy.toast.open({
                                message: "Premises has been created successfully",
                                type: "is-success",
                            });
                    })
                    .catch((error) => {
                        loadingComponent.close();
                        this.showPremises = false
                        API.APILog(error);
                    });
            } else {
                API.update_premises(this.token, this.newPremises.uuid, data).then((response) => {
                    this.fetchPremises()

                    // Also refresh user's personal premises list
                    this.$store.dispatch('app/refreshPremises').catch(error => {
                        console.error('Failed to refresh user premises:', error);
                    });

                    loadingComponent.close();

                    this.showPremises = false

                    if (response)
                        this.$buefy.toast.open({
                            message: "Premises has been updated successfully",
                            type: "is-success",
                        });
                }).catch((error) => {
                    loadingComponent.close();
                    this.showPremises = false
                    API.APILog(error);
                });
            }

        },
        addGroup() {

            let json_data = {
                name: this.newGroup.name,
                premises_organization: this.newGroup.organization.uuid,
                manager_uuid: this.newGroup.manager.uuid
            };

            const loadingComponent = this.$buefy.loading.open({ container: null });

            if(this.openForUpdate) {
                API.update_premises_group(this.token, this.newGroup.uuid, json_data).then((response) => {
                    this.fetchPremises()

                    loadingComponent.close();

                    this.showGroup = false

                    if (response)
                        this.$buefy.toast.open({
                            message: "Premises group has been updated successfully",
                            type: "is-success",
                        });
                }).catch((error) => {
                    loadingComponent.close();
                    this.showGroup = false
                    API.APILog(error);
                });
            } else {
            API.create_premises_group(this.token, json_data)
                .then((responses) => {

                    this.showGroup = false;

                    this.fetchPremises()

                    if (responses)
                        this.$buefy.toast.open({
                            message: "Premises group has been created successfully",
                            type: "is-success",
                        });

                    loadingComponent.close();


                })
                .catch((error) => {
                    this.showGroup = false;
                    loadingComponent.close();
                    API.APILog(error);
                });
            }

        },
        addOrganization() {

            let json_data = {
                name: this.newOrganization.name,
            };

            const loadingComponent = this.$buefy.loading.open({ container: null });

            if(this.openForUpdate) {
                API.update_premises_organization(this.token, this.newOrganization.uuid, json_data).then((response) => {
                    
                    this.fetchPremises()

                    loadingComponent.close();

                    this.showOrgnization = false

                    if (response)
                        this.$buefy.toast.open({
                            message: "Premises group has been updated successfully",
                            type: "is-success",
                        });
                }).catch((error) => {
                    loadingComponent.close();
                    this.showOrgnization = false
                    API.APILog(error);
                });
            } else {


            API.create_premises_organization(this.token, json_data)
                .then((responses) => {

                    loadingComponent.close();

                    if (responses)
                        this.$buefy.toast.open({
                            message: "Premises organization has been created successfully",
                            type: "is-success",
                        });

                    this.fetchPremises()

                    this.showOrgnization = false

                })
                .catch((error) => {
                    loadingComponent.close();
                    API.APILog(error);
                });
            }
        },
        editPremises(prem) {
            this.newPremises = Object.assign({}, prem)
            this.showPremises = true;
            this.openForUpdate = true;
        },
        editGroup(group) {
            this.newGroup = _.cloneDeep(group);

            const orgInOptions = this.organizationOptions.find(o => o.uuid === this.newGroup.organization.uuid);
            if (orgInOptions) {
                this.newGroup.organization = orgInOptions;
            }

            if (this.newGroup.organization && this.newGroup.organization.uuid) {
                this.filterManagers = this.managers.filter(manager =>
                    manager.organizations && manager.organizations.some(org => org.uuid === this.newGroup.organization.uuid)
                );
            } else {
                this.filterManagers = [];
            }

            if (this.newGroup.manager && this.newGroup.manager.uuid) {
                const managerInOptions = this.filterManagers.find(m => m.uuid === this.newGroup.manager.uuid);
                if (managerInOptions) {
                    this.newGroup.manager = managerInOptions;
                } else {
                    const currentManager = this.managers.find(m => m.uuid === this.newGroup.manager.uuid);
                    if (currentManager) {
                        this.filterManagers.unshift(currentManager);
                        this.newGroup.manager = currentManager;
                    }
                }
            }
            
            this.showGroup = true;
            this.openForUpdate = true;
        },
        editOrganization(org) {
            this.newOrganization = Object.assign({}, org);
            this.showOrgnization = true;
            this.openForUpdate = true;

        },
        deletePremises(uuid) {

            if (uuid) {

                this.$buefy.dialog.confirm({
                    title: "Deleting a premises",
                    message:
                        "Are you sure you want to <b>delete</b> a premise? This action cannot be undone.",
                    confirmText: "Delete",
                    type: "is-danger",
                    hasIcon: true,
                    onConfirm: () => {



                        const loadingComponent = this.$buefy.loading.open({ container: null });
                        API.delete_premises(this.token, uuid)
                            .then(() => {

                                this.fetchPremises()

                                loadingComponent.close();

                                this.$buefy.toast.open({
                                    message: "A permises has been deleted successfully",
                                    type: "is-success",
                                });
                            })
                            .catch((error) => {
                                loadingComponent.close();
                                API.APILog(error);
                            });
                    },
                });
            } else {

                this.$buefy.dialog.alert(
                    "No premises found."
                );
            }
        },
        deleteOrganization(uuid) {
            if (uuid) {
                this.$buefy.dialog.confirm({
                    title: "Deleting an organization",
                    message:
                        "Are you sure you want to <b>delete</b> a premise organization? This action cannot be undone.",
                    confirmText: "Delete",
                    type: "is-danger",
                    hasIcon: true,
                    onConfirm: () => {
                        const loadingComponent = this.$buefy.loading.open({ container: null });
                        API.delete_premises_organization(this.token, uuid)
                            .then(() => {

                                this.fetchPremises()
                                loadingComponent.close();
                                this.$buefy.toast.open({
                                    message: "A permises organization has been deleted successfully",
                                    type: "is-success",
                                });
                            })
                            .catch((error) => {

                                loadingComponent.close();
                                API.APILog(error);
                            });
                    },
                });
            } else {
                this.$buefy.dialog.alert(
                    "No audit found. If you expect to see a new audit, try logging out first."
                );
            }
        },
        deleteGroup(uuid) {
            if (uuid) {
                this.$buefy.dialog.confirm({
                    title: "Deleting a group",
                    message:
                        "Are you sure you want to <b>delete</b> a premise group? This action cannot be undone.",
                    confirmText: "Delete",
                    type: "is-danger",
                    hasIcon: true,
                    onConfirm: () => {

                        const loadingComponent = this.$buefy.loading.open({ container: null });
                        API.delete_premises_group(this.token, uuid)
                            .then(() => {

                                this.fetchPremises()
                                loadingComponent.close();
                                this.$buefy.toast.open({
                                    message: "A permises group has been deleted successfully",
                                    type: "is-success",
                                });
                            })
                            .catch((error) => {

                                loadingComponent.close();
                                API.APILog(error);
                            });
                    },
                });
            } else {
                this.$buefy.dialog.alert(
                    "No audit found. If you expect to see a new audit, try logging out first."
                );
            }
        },
        onBack() {
            this.currentAuditIndex = -1;
            this.$router.push({ name: "home" });
            console.log("User voluntarily stopped location selection");
        },

        mapPremises(results) {
            this.premises = results.map(({ uuid, name, group_uuid, group }) => ({
                uuid,
                name,
                group: {
                    uuid: group_uuid,
                    name: group
                }
            }))
        },

        mapGroups(results) {
            this.groups = results.map(({ uuid, name, organization, organization_uuid, manager_name, manager_uuid}) => ({
                uuid,
                name,
                organization: {
                    uuid: organization_uuid,
                    name: organization
                },
                manager: {
                    name: manager_name,
                    uuid: manager_uuid
                }
            }));

            this.groupOptions.push(...this.groups.map(({ name, uuid }) => ({ name, uuid })));
            this.groupOptions = _.uniqBy(this.groupOptions, 'uuid');
        },

        fetchPremises() {
            const loadingComponent = this.$buefy.loading.open({ container: null });
            Promise.all([
                API.fetch_premises_sort(this.token, 'asc'),
                API.fetch_groups_sort(this.token, 'asc'),
                API.fetch_organizations_sort(this.token, 'asc'),
                API.fetch_managers(this.token)
            ])
                .then((responses) => {
                    console.log(responses)
                    this.mapPremises(responses[0].data["premises"])
                    this.mapGroups(responses[1].data["groups"])
                    this.organizations = responses[2].data["organizations"];
                    this.organizationOptions = this.organizations.map(({ name, uuid }) => ({ name, uuid }))
                    this.managers = responses[3].data["managers"]
                    loadingComponent.close();
                })
                .catch((error) => {
                    API.APILog(error);
                    loadingComponent.close();
                    this.$buefy.dialog.alert("Error fetching premises information.");
                });
        },

        fetchPremisesPaginated() {
            const loadingComponent = this.$buefy.loading.open({ container: null });
            Promise.all([
                API.fetch_premises_paginated(
                    this.token, 
                    this.currentPage, 
                    this.itemsPerPage, 
                    this.sortBy, 
                    this.sortDirection,
                    this.premisesFilters.name || null,
                    this.premisesFilters.groupName || null
                ),
                API.fetch_groups_sort(this.token, 'asc'),
                API.fetch_organizations_sort(this.token, 'asc'),
                API.fetch_managers(this.token)
            ])
                .then((responses) => {
                    console.log('Paginated response:', responses)
                    this.mapPremises(responses[0].data["premises"] || []);
                    this.premisesTotalRows = responses[0].data["total_results"] || 0;
                    this.mapGroups(responses[1].data["groups"])
                    this.organizations = responses[2].data["organizations"];
                    this.organizationOptions = this.organizations.map(({ name, uuid }) => ({ name, uuid }))
                    this.managers = responses[3].data["managers"]
                    loadingComponent.close();
                })
                .catch((error) => {
                    API.APILog(error);
                    loadingComponent.close();
                    this.$buefy.dialog.alert("Error fetching premises information.");
                });
        }
    },

    created() {
        // Create debounced function for premises filter changes
        this.debouncedPremisesFilterChange = this.debounce(() => {
            this.currentPage = 1;
            this.premisesPage = 1;
            this.fetchPremisesPaginated();
        }, 500);

        // Use the new paginated fetch for premises tab
        this.fetchPremisesPaginated();

        // Refresh user's personal premises list
        this.$store.dispatch('app/refreshPremises').catch(error => {
            console.error('Failed to refresh user premises:', error);
        });
    },
};
</script>


<style scoped>
.dropdown-box {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px;
}
.location-box {
  background-color: #fff;
  position: absolute;
  width: 200px;
  max-height: 400px;
  box-sizing: border-box;
  box-shadow: -1px 5px 15px -3px rgba(0,0,0,0.75);
  border-radius: 8px;
  padding: 8px;
  font-size: 17px;
  z-index: 999;
  overflow-y: auto;
}
</style>